import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@chainlit/react-client';
import { useDropzone } from 'react-dropzone';
import {
  ArrowLeft, Plus, Trash2, X, FileText, Upload, Loader2,
  CheckCircle2, AlertCircle, Clock,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { kbApi, type KBDetail, type KBFile } from '@/api/kb';
import { useAppConfig } from '@/hooks/useAppConfig';
import { KB_ACCEPTED_TYPES, KB_FORMATS_LABEL } from '@/lib/uploadFormats';

function StatusBadge({ status }: { status: string }) {
  if (status === 'ready')
    return (
      <span className="inline-flex items-center gap-1 text-green-600 text-xs">
        <CheckCircle2 className="size-3" /> 就緒
      </span>
    );
  if (status === 'error')
    return (
      <span className="inline-flex items-center gap-1 text-red-500 text-xs">
        <AlertCircle className="size-3" /> 錯誤
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-yellow-500 text-xs">
      <Clock className="size-3 animate-pulse" /> 處理中
    </span>
  );
}

export default function KnowledgeBaseDetail() {
  const navigate = useNavigate();
  const { id }   = useParams<{ id: string }>();
  const { user } = useAuth();
  const appConfig = useAppConfig();
  // 單機版免登入，useAuth() 無 user（role undefined），但後端一律以本機
  // super_admin 身分處理——前端比照放行（同 index.tsx）
  const singleUserMode = !!appConfig?.single_user_mode;
  const role = (user?.metadata as Record<string, unknown>)?.role as string | undefined;
  const isSuperAdmin = singleUserMode || role === 'super_admin';
  const canManageKb = isSuperAdmin || role === 'org_user';

  const [kb, setKb]                     = useState<KBDetail | null>(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteKBOpen, setDeleteKBOpen] = useState(false);
  const [uploadFiles, setUploadFiles]   = useState<File[]>([]);
  const [uploading, setUploading]       = useState(false);
  const [deleteDocId, setDeleteDocId]   = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchKB = async () => {
    if (!id) return;
    try {
      const data = await kbApi.get(id);
      setKb(data);
      // 只要所有檔案都是終態（ready / error），才停止輪詢
      // 後端 IngestionAgent 使用 'ingesting' 而非 'processing'，
      // 用 !ready && !error 涵蓋所有進行中的狀態
      const hasActive = data.files.some(
        (f) => f.status !== 'ready' && f.status !== 'error'
      );
      if (!hasActive && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    fetchKB().finally(() => setLoading(false));
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Start polling when active (non-terminal) files appear
  useEffect(() => {
    if (!kb) return;
    const hasActive = kb.files.some(
      (f) => f.status !== 'ready' && f.status !== 'error'
    );
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(fetchKB, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kb]);

  const onDrop = useCallback((accepted: File[]) => {
    setUploadFiles((prev) => [...prev, ...accepted]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: KB_ACCEPTED_TYPES,
    maxSize: 512 * 1024 * 1024,
  });

  const handleUpload = async () => {
    if (!id || uploadFiles.length === 0 || uploading) return;
    setUploading(true);
    try {
      const newFiles = await kbApi.uploadDocs(id, uploadFiles);
      setKb((prev) =>
        prev ? { ...prev, files: [...prev.files, ...(newFiles as KBFile[])] } : prev
      );
      setUploadFiles([]);
      setAddModalOpen(false);
      // Start polling for newly processing files
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchKB, 3000);
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async () => {
    if (!id || !deleteDocId) return;
    try {
      await kbApi.deleteDoc(id, deleteDocId);
      setKb((prev) =>
        prev ? { ...prev, files: prev.files.filter((f) => f.id !== deleteDocId) } : prev
      );
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleteDocId(null);
    }
  };

  const handleDeleteKB = async () => {
    if (!id) return;
    try {
      await kbApi.delete(id);
      navigate('/knowledge-base');
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
        <Loader2 className="size-5 animate-spin" /> 載入中…
      </div>
    );

  if (error || !kb)
    return (
      <div className="p-6 text-red-500 text-sm">
        {error || '知識庫不存在'}{' '}
        <Button variant="link" onClick={() => navigate('/knowledge-base')}>
          返回列表
        </Button>
      </div>
    );

  return (
    <div className="p-6 space-y-5 max-w-4xl mx-auto">
      {/* 頂部操作列 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-foreground">{kb.name}</h2>
        <div className="flex items-center gap-2">
          {canManageKb && (
            <>
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
                onClick={() => setAddModalOpen(true)}
              >
                <Plus className="size-4 mr-1" />
                添加文件
              </Button>
              <Button variant="destructive" onClick={() => setDeleteKBOpen(true)}>
                <Trash2 className="size-4 mr-1" />
                刪除知識庫
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate('/knowledge-base')}>
            <ArrowLeft className="size-4 mr-1" />
            返回列表
          </Button>
        </div>
      </div>

      {/* 知識庫資訊 */}
      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border bg-accent/30">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            知識庫資訊
          </h3>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {(
              [
                ['名稱',    kb.name],
                ['描述',    kb.description || '沒有描述'],
                ['分段大小', `${kb.chunk_size.toLocaleString()} tokens`],
                ['文件數量', `${kb.files.length} 個`],
              ] as [string, string][]
            ).map(([label, value]) => (
              <tr key={label} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-medium text-muted-foreground bg-muted border-r border-border w-32">
                  {label}
                </td>
                <td className="px-4 py-3 text-foreground">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 文件列表 */}
      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border bg-accent/30">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            知識庫內容
          </h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">索引</TableHead>
              <TableHead>來源檔名</TableHead>
              <TableHead className="w-24">狀態</TableHead>
              <TableHead className="w-16 text-right">區塊</TableHead>
              <TableHead className="w-28 text-right">Tokens</TableHead>
              <TableHead className="w-14 text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {kb.files.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  尚無文件，點擊「添加文件」開始上傳
                </TableCell>
              </TableRow>
            )}
            {kb.files.map((doc, index) => (
              <TableRow key={doc.id}>
                <TableCell className="text-muted-foreground">#{index + 1}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FileText className="size-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate max-w-xs">{doc.file_name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <StatusBadge status={doc.status} />
                </TableCell>
                <TableCell className="text-right text-muted-foreground text-sm">
                  {doc.status === 'ready' ? doc.total_chunks : '—'}
                </TableCell>
                <TableCell className="text-right text-muted-foreground text-sm">
                  {doc.status === 'ready' ? doc.total_tokens.toLocaleString() : '—'}
                </TableCell>
                {canManageKb && (
                  <TableCell>
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="刪除"
                        onClick={() => setDeleteDocId(doc.id)}
                      >
                        <Trash2 className="size-4 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* 添加文件 Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>添加文件到知識庫</DialogTitle>
          </DialogHeader>

          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary hover:bg-secondary'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="size-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">拖放或點擊選擇文件</p>
            <p className="text-xs text-muted-foreground mt-1">
              支援 {KB_FORMATS_LABEL}，總大小不超過 512 MB
            </p>
          </div>

          {uploadFiles.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">已選擇的檔案：</p>
              {uploadFiles.map((file, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-secondary rounded px-3 py-1.5 text-sm"
                >
                  <span className="text-foreground truncate">{file.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={() => setUploadFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => { setAddModalOpen(false); setUploadFiles([]); }}
              disabled={uploading}
            >
              取消
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleUpload}
              disabled={uploadFiles.length === 0 || uploading}
            >
              {uploading ? (
                <><Loader2 className="size-4 mr-2 animate-spin" />上傳中…</>
              ) : (
                '上傳並處理'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 刪除文件確認 */}
      <AlertDialog open={!!deleteDocId} onOpenChange={(open) => !open && setDeleteDocId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除文件</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除此文件及其所有向量化資料嗎？此操作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDoc} className="bg-red-500 hover:bg-red-600">
              確認刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 刪除知識庫確認 */}
      <AlertDialog open={deleteKBOpen} onOpenChange={setDeleteKBOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除知識庫</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除知識庫「{kb.name}」嗎？所有文件與向量資料將一併刪除，此操作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteKB} className="bg-red-500 hover:bg-red-600">
              確認刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
